import type { KeyboardEvent, ReactNode } from 'react';
import { ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

/** Sólo direcciones web de verdad: nunca `javascript:` ni rutas sueltas. */
export const esUrlDeProducto = (url?: string | null): url is string =>
  !!url && /^https?:\/\//i.test(url.trim());

/**
 * Un precio (o un nombre) que abre el producto EXACTO en la tienda, para
 * corroborarlo (pedido de Joaquín, 26-oct: «en cualquier parte que esté el precio
 * del competidor, que sea un vínculo»). Al pasar el cursor se subraya y aparece el
 * ícono de «abrir afuera»; abre en una pestaña nueva y no dispara el clic de la
 * fila que lo contiene. Sin dirección, se dibuja tal cual, sin enlace.
 */
export function EnlaceAProducto({
  href,
  tienda,
  children,
  className,
  sinIcono = false,
}: {
  href?: string | null;
  /** Para el texto de ayuda: «Ver en Farmacorp». */
  tienda?: string | null;
  children: ReactNode;
  className?: string;
  /** En celdas muy angostas, sin el ícono. */
  sinIcono?: boolean;
}) {
  if (!esUrlDeProducto(href)) return <>{children}</>;
  return (
    <a
      href={href.trim()}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title={tienda ? `Ver el producto en ${tienda}` : 'Ver el producto en la tienda'}
      className={cn(
        'group/enlace inline-flex cursor-pointer items-center gap-1 rounded-sm underline-offset-2 hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className
      )}
    >
      {children}
      {!sinIcono && (
        <ExternalLink
          aria-hidden="true"
          className="size-3 shrink-0 opacity-0 transition-opacity group-hover/enlace:opacity-100 group-focus-visible/enlace:opacity-100"
        />
      )}
      <span className="sr-only"> (abre la tienda en una pestaña nueva)</span>
    </a>
  );
}

/**
 * Las props de una fila que se toca entera (abre un detalle) y adentro lleva
 * enlaces: un `<button>` no puede contener un `<a>`, así que la fila es un
 * contenedor con rol de botón, que se activa con clic, Enter o Espacio.
 */
export function filaClicable(alTocar: () => void) {
  return {
    role: 'button' as const,
    tabIndex: 0,
    onClick: alTocar,
    onKeyDown: (e: KeyboardEvent) => {
      if (e.target !== e.currentTarget) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        alTocar();
      }
    },
  };
}
